/**
 * Playing Cards Component
 *
 * Renders playing cards using SVG images for blackjack questions.
 */

import styles from './PlayingCards.module.css';

type Suit = 'hearts' | 'diamonds' | 'clubs' | 'spades';
type CardValue = 'A' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K';

interface Card {
  value: CardValue;
  suit: Suit;
}

interface BlackjackConfig {
  playerCards: Card[];
  dealerCard: Card;
  handType: 'hard' | 'soft' | 'pair';
  handValue: number | string;
}

// Map suit to file suffix
const SUIT_CODE: Record<Suit, string> = {
  clubs: 'C',
  diamonds: 'D',
  hearts: 'H',
  spades: 'S',
};

// Map card value to file prefix (10 becomes T)
const VALUE_CODE: Record<CardValue, string> = {
  'A': 'A',
  '2': '2',
  '3': '3',
  '4': '4',
  '5': '5',
  '6': '6',
  '7': '7',
  '8': '8',
  '9': '9',
  '10': 'T',
  'J': 'J',
  'Q': 'Q',
  'K': 'K',
};

// Get the image path for a card (format: {rank}{suit}.svg, e.g., AS.svg, TH.svg)
function getCardImagePath(card: Card): string {
  const valueCode = VALUE_CODE[card.value];
  const suitCode = SUIT_CODE[card.suit];
  return `/cards/${valueCode}${suitCode}.svg`;
}

interface PlayingCardProps {
  card: Card;
  faceDown?: boolean;
  size?: 'small' | 'medium' | 'large';
}

export function PlayingCard({ card, faceDown = false, size = 'medium' }: PlayingCardProps) {
  const sizeClass = styles[size];

  if (faceDown) {
    return (
      <div className={`${styles.card} ${sizeClass} ${styles.faceDown}`}>
        <img src="/cards/1B.svg" alt="Face down card" className={styles.cardImage} />
      </div>
    );
  }

  return (
    <div className={`${styles.card} ${sizeClass}`}>
      <img
        src={getCardImagePath(card)}
        alt={`${card.value} of ${card.suit}`}
        className={styles.cardImage}
      />
    </div>
  );
}

interface BlackjackHandProps {
  config: BlackjackConfig;
}

export function BlackjackHand({ config }: BlackjackHandProps) {
  const { playerCards, dealerCard, handType, handValue } = config;

  // Format hand description
  const getHandDescription = () => {
    if (handType === 'pair') {
      const cardVal = String(handValue).split(',')[0];
      if (cardVal === 'A') return 'Pair of Aces';
      if (cardVal === '10') return 'Pair of Tens';
      return `Pair of ${cardVal}s`;
    }
    if (handType === 'soft') {
      return `Soft ${handValue}`;
    }
    return `Hard ${handValue}`;
  };

  return (
    <div className={styles.blackjackTable}>
      {/* Dealer section */}
      <div className={styles.dealerSection}>
        <div className={styles.sectionLabel}>Dealer</div>
        <div className={styles.cardRow}>
          <PlayingCard card={dealerCard} size="large" />
          <PlayingCard card={{ value: 'A', suit: 'spades' }} faceDown size="large" />
        </div>
      </div>

      {/* Divider */}
      <div className={styles.tableDivider} />

      {/* Player section */}
      <div className={styles.playerSection}>
        <div className={styles.sectionLabel}>Your Hand: {getHandDescription()}</div>
        <div className={styles.cardRow}>
          {playerCards.map((card, index) => (
            <PlayingCard key={index} card={card} size="large" />
          ))}
        </div>
      </div>
    </div>
  );
}

export type { Card, BlackjackConfig };
